/**
 * Typed localStorage access. SPEC.md §6.4, §10.2.
 *
 * Storage is not guaranteed: Safari private browsing throws on write, embedded
 * webviews can disable it entirely, and a quota error must never take down a
 * game in progress. Every access here degrades to "no stored value" instead.
 *
 * Key names come from the shared package so the server-side documentation of
 * them and the client cannot drift.
 */

import { playerTokenSchema, playerTokenStorageKey } from '@card-counter/shared';

function backingStore(): Storage | null {
  try {
    const store = globalThis.localStorage;
    if (!store) return null;
    // Accessing the property can succeed while any use throws, so probe once.
    const probe = '__card-counter:probe__';
    store.setItem(probe, '1');
    store.removeItem(probe);
    return store;
  } catch {
    return null;
  }
}

function readRaw(key: string): string | null {
  try {
    return backingStore()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string): void {
  try {
    backingStore()?.setItem(key, value);
  } catch {
    /* Persistence is a convenience here; losing it is not an error. */
  }
}

function removeRaw(key: string): void {
  try {
    backingStore()?.removeItem(key);
  } catch {
    /* ignore */
  }
}

// --- Player token (§6.4) -----------------------------------------------------

/**
 * The reconnect token for one session. Validated on read so a truncated or
 * hand-edited value is treated as absent rather than sent to the server.
 */
export function readPlayerToken(sessionId: string): string | null {
  const raw = readRaw(playerTokenStorageKey(sessionId));
  if (raw === null) return null;
  const parsed = playerTokenSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

export function writePlayerToken(sessionId: string, playerToken: string): void {
  writeRaw(playerTokenStorageKey(sessionId), playerToken);
}

export function clearPlayerToken(sessionId: string): void {
  removeRaw(playerTokenStorageKey(sessionId));
}

